import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { PRODUCT, formatPrice, getColor } from "../data/catalog";

const DELIVERY = [
  { id: "standard", label: "Standard", note: "[delivery time]", price: 0 },
  { id: "express", label: "Express", note: "[delivery time]", price: 15 },
];
const field = "w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-graphite focus:border-ink";
const FIELDS = [["email", "Email", "email", "col-span-2"], ["first_name", "First name"], ["last_name", "Last name"], ["address", "Address", "text", "col-span-2"], ["city", "City"], ["postal_code", "Postal code"], ["country", "Country", "text", "col-span-2"]];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", address: "", city: "", postal_code: "", country: "" });
  const [delivery, setDelivery] = useState("standard");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const ship = DELIVERY.find((d) => d.id === delivery).price;

  const place = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, delivery, items: items.map((i) => ({ color_id: i.colorId, qty: i.qty })) });
      clear();
      nav(`/order/${data.order_number}`, { state: data });
    } catch {
      toast.error("Could not place the order. Please check your details.");
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <div className="grid min-h-[70vh] place-items-center bg-ivory pt-32 text-center" data-testid="checkout-empty">
        <div><p className="font-display text-4xl text-ink">Your bag is empty.</p><Link to="/shop" className="btn-dark mt-6">Shop the collection</Link></div>
      </div>
    );
  }

  return (
    <div className="bg-ivory pb-24 pt-32 md:pt-40" data-testid="checkout-page">
      <form onSubmit={place} className="mx-auto grid max-w-[1300px] gap-12 px-5 md:px-10 lg:grid-cols-12">
        <div className="space-y-10 lg:col-span-7">
          <div className="border border-[#C9B98F] bg-[#FBF6E9] px-4 py-3 text-xs text-[#5C4A1A]" data-testid="checkout-demo-notice">
            DEMO CHECKOUT — no payment is taken. In the live store this step is handled by Shopify's secure checkout.
          </div>
          <section>
            <h1 className="font-display text-4xl font-light text-ink">Checkout</h1>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {FIELDS.map(([k, ph, type = "text", span = "col-span-1"]) => (
                <input key={k} required type={type} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${field} ${span}`} data-testid={`checkout-${k}-input`} />
              ))}
            </div>
          </section>
          <section>
            <p className="label-mono mb-4">Delivery</p>
            <div className="space-y-2">
              {DELIVERY.map((d) => (
                <label key={d.id} className={`flex cursor-pointer items-center justify-between border px-4 py-4 text-sm transition-colors ${delivery === d.id ? "border-ink bg-white" : "border-line"}`} data-testid={`checkout-delivery-${d.id}`}>
                  <span className="flex items-center gap-3"><input type="radio" name="delivery" checked={delivery === d.id} onChange={() => setDelivery(d.id)} className="accent-black" />{d.label} <span className="text-graphite">· {d.note}</span></span>
                  <span>{d.price ? formatPrice(d.price) : "Free"} <span className="text-[10px] text-graphite">(sample)</span></span>
                </label>
              ))}
            </div>
          </section>
          <section>
            <p className="label-mono mb-4">Payment</p>
            <div className="border border-line bg-white px-4 py-5 text-sm text-graphite"><Lock className="mb-2 h-4 w-4 text-ink" />Card, Shop Pay, Apple Pay and Google Pay will be available through Shopify Checkout. (MOCKED in this prototype.)</div>
          </section>
          <button disabled={busy} className="btn-dark w-full" data-testid="checkout-place-order-button">{busy ? "Placing order…" : `Place order · ${formatPrice(subtotal + ship)}`}</button>
        </div>
        <aside className="h-fit bg-white p-6 md:p-8 lg:sticky lg:top-32 lg:col-span-5" data-testid="checkout-summary">
          <p className="label-mono">Order summary</p>
          <ul className="mt-5 divide-y divide-line">
            {items.map((i) => { const c = getColor(i.colorId); return (
              <li key={i.colorId} className="flex items-center gap-4 py-4">
                <div className="relative h-16 w-16 bg-stone"><img src={c.image} alt="" className="h-full w-full object-contain mix-blend-multiply" /><span className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-ink text-[10px] text-ivory">{i.qty}</span></div>
                <div className="flex-1 text-sm"><p className="text-ink">{PRODUCT.name}</p><p className="text-xs text-graphite">{c.name}</p></div>
                <span className="text-sm">{formatPrice(i.qty * PRODUCT.price)}</span>
              </li>); })}
          </ul>
          <dl className="mt-4 space-y-2 border-t border-line pt-4 text-sm">
            <div className="flex justify-between"><dt className="text-graphite">Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-graphite">Shipping</dt><dd>{ship ? formatPrice(ship) : "Free"}</dd></div>
            <div className="flex justify-between border-t border-line pt-3 text-base"><dt>Total</dt><dd data-testid="checkout-total">{formatPrice(subtotal + ship)}</dd></div>
          </dl>
        </aside>
      </form>
    </div>
  );
}
